import { DateTime } from 'luxon';
import { useCallback } from 'react';

import Container from './Container';
import DayCell from './DayCell';
import Header from './Header';
import HeaderCell from './HeaderCell';
import { useCalendarDates } from './hooks';
import Navigation from './Navigation';
import NavigationButton from './NavigationButton';
import Row from './Row';
import Title from './Title';

import { endOfDay, format, isBefore, isSameMonth, startOfDay } from '@/app/utils/date';

interface Props {
	value?: { start: DateTime; end: DateTime };
	onChange?: (value: { start: DateTime; end: DateTime }) => void;
	selectionMode?: 'daily' | 'weekly' | 'custom';
	focusDate?: DateTime;
	containerClassName?: string;
	selectedCellClassName?: string;
	tooltip?: string | string[];
	isValid?: boolean;
	disableBeforeDate?: DateTime;
}

const ExperimentalCalendar = ({
	focusDate,
	value = { start: startOfDay(DateTime.now()), end: endOfDay(DateTime.now()) },
	onChange,
	containerClassName,
	selectedCellClassName,
	selectionMode = 'daily',
	tooltip = ['', ''],
	isValid = true,
	disableBeforeDate,
}: Props) => {
	const [
		weeks,
		{ prev, next, date, isDateSelected, isWeekSelected, isLastSelectedDate, isFirstSelected },
	] = useCalendarDates(value, focusDate);

	const handleClicked = useCallback(
		(week: { week: number; dates: DateTime[] }, date: DateTime) => {
			return () => {
				if (!onChange) {
					return;
				}

				switch (selectionMode) {
					case 'custom':
					case 'daily': {
						onChange({
							start: startOfDay(date),
							end: endOfDay(date),
						});
						break;
					}
					case 'weekly': {
						onChange({
							start: startOfDay(week.dates[0]),
							end: endOfDay(week.dates[6]),
						});
						break;
					}
					default: {
						break;
					}
				}
			};
		},
		[onChange, selectionMode]
	);

	return (
		<Container className={containerClassName}>
			<Navigation>
				<NavigationButton direction="left" onClick={prev} tooltip={tooltip[2]} />
				<Title isValid={isValid} date={date} />
				<NavigationButton direction="right" onClick={next} tooltip={tooltip[1]} />
			</Navigation>
			<Header>
				<HeaderCell>#</HeaderCell>
				<HeaderCell>M</HeaderCell>
				<HeaderCell>T</HeaderCell>
				<HeaderCell>W</HeaderCell>
				<HeaderCell>T</HeaderCell>
				<HeaderCell>F</HeaderCell>
				<HeaderCell>S</HeaderCell>
				<HeaderCell>S</HeaderCell>
			</Header>
			{weeks.map((week, weekIndex) => {
				return (
					<Row key={weekIndex}>
						<DayCell
							isSelected={selectionMode === 'weekly' && isWeekSelected(week.week)}
							isFirstSelectedCell={selectionMode === 'weekly'}
						>
							<span>{week.week}</span>
						</DayCell>

						{week.dates.map((current, index) => {
							return (
								<DayCell
									selectedClassName={selectedCellClassName}
									isSelected={isDateSelected(current)}
									isNotValid={
										!isSameMonth(current, date) ||
										(disableBeforeDate
											? isBefore(current, disableBeforeDate)
											: false)
									}
									isClickable={
										disableBeforeDate
											? !isBefore(current, disableBeforeDate)
											: true
									}
									isFirstSelectedCell={
										selectionMode !== 'weekly' && isFirstSelected(current)
									}
									isLastSelectedCell={isLastSelectedDate(current)}
									onClick={handleClicked(week, current)}
									key={index}
									tooltip={tooltip[0]}
								>
									{format(current, 'd')}
								</DayCell>
							);
						})}
					</Row>
				);
			})}
		</Container>
	);
};

export default ExperimentalCalendar;
